/**
 * R5 — Feed de presse : types, constantes, merge, migration legacy.
 *
 * V3 — `HeadlineEntry` enrichi (text + urgency + source) pour que le
 * PressTicker puisse styler par catégorie. Migration douce des saves
 * antérieures (`string[]` → `HeadlineEntry[]`).
 */

export type HeadlineUrgency =
  | "initial"
  | "fait_divers"
  | "incident"
  | "crise_locale"
  | "crise_majeure"
  | "cohorte"
  | "checkpoint";

export interface HeadlineEntry {
  text: string;
  urgency: HeadlineUrgency;
  source?: string;
}

export const INITIAL_HEADLINES: HeadlineEntry[] = [
  { text: "Un nouveau président prend ses fonctions", urgency: "initial" },
  {
    text: "La Cour des comptes rappelle les engagements pris",
    urgency: "initial",
  },
  { text: "Les marchés observent", urgency: "initial" },
  {
    text: "Un éditorialiste : « tout est à faire, rien n'est joué »",
    urgency: "initial",
  },
  { text: "Investiture sobre et sans flamme", urgency: "initial" },
];

/** Capacité maximale du feed de presse — au-delà, on rote l'ancien.
 *  16 → 60 (2026-07-15) : la save persiste le feed, et une fenêtre de
 *  16 rendait les parties inanalysables a posteriori (jalons T10/T30/T38
 *  hors fenêtre). Le ticker, lui, n'affiche que les plus récentes
 *  (cf. PressTicker TICKER_WINDOW). */
export const HEADLINE_FEED_MAX = 60;

/**
 * V3 — Combine les événements (L1..L4) + headlines des cohortes (I3) avec
 * le feed précédent. Préserve `urgency` et `source` pour que PressTicker
 * puisse styler par catégorie. Voix des cohortes en tête (plus saillantes).
 */
export function mergeEventsIntoHeadlines(
  events: { narrative: string; urgency?: string }[],
  cohortHeadlines: { text: string; source?: string }[],
  previous: HeadlineEntry[],
): HeadlineEntry[] {
  const fresh: HeadlineEntry[] = [];
  for (const h of cohortHeadlines ?? []) {
    if (h.text && h.text.length > 0) {
      // L5a — les checkpoints sont reconnus à leur source préfixée
      // "BILAN ·" et promus en urgency=checkpoint pour styliser le
      // bandeau plein largeur dans le ticker.
      const isCheckpoint = (h.source ?? "").startsWith("BILAN ·");
      fresh.push({
        text: h.text,
        urgency: isCheckpoint ? "checkpoint" : "cohorte",
        source: h.source,
      });
    }
  }
  for (const e of events ?? []) {
    if (e.narrative && e.narrative.length > 0) {
      const urgency =
        (e.urgency as HeadlineUrgency | undefined) ?? "fait_divers";
      fresh.push({ text: e.narrative, urgency });
    }
  }
  if (fresh.length === 0) return previous;
  return [...fresh, ...previous].slice(0, HEADLINE_FEED_MAX);
}

/**
 * V3 — migre une vieille save (`string[]`) vers le nouveau format
 * `HeadlineEntry[]`. Conserve les nouvelles saves intactes.
 */
export function coerceHeadlineFeed(raw: unknown): HeadlineEntry[] {
  if (!Array.isArray(raw)) return INITIAL_HEADLINES;
  if (raw.length === 0) return INITIAL_HEADLINES;
  if (
    typeof raw[0] === "object" &&
    raw[0] !== null &&
    "text" in (raw[0] as object)
  ) {
    return raw as HeadlineEntry[];
  }
  return (raw as string[]).map((s) => ({
    text: s,
    urgency: "initial" as const,
  }));
}
